import React, { ReactElement } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Upload, message } from 'antd';
import { RcFile } from 'antd/es/upload/interface';
import ctx from 'classnames';

import { CogIcon } from '../CogIcon';
import { cognitoUser } from '../../../features/auth/authSlice';
import { Microservices } from '../../../app/AllowedMicroservices';
import { BASE_URL } from '../../../app/apiMiddleware';

import uploadIcon from '../../../assets/admin/upload.svg';

import styles from './index.module.scss';
import { getTranslations } from '../../../features/translations/translationsUtils';
import { useDeviceSize } from '../../utils/useDeviceSize';
import { SIZES } from '../screen';

interface UploadButtonProps {
    loading?: boolean;
    image: ReactElement | boolean;
    showClear?: boolean;
    onClear?: () => void;
}
interface UploadButtonForProfileProps {
    showClear?: boolean;
    onClear?: () => void;
}

interface UploadImageProps {
    loading?: boolean;
    onHandleChange: any;
    imageUrl?: string;
    showClear?: boolean;
    onClear?: () => void;
}


function beforeUpload(file: RcFile) {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
        message.error('You can only upload JPG/PNG file!');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
        message.error('Image must smaller than 2MB!');
    }
    return isJpgOrPng && isLt2M;
}

export const UploadButton: React.FunctionComponent<UploadButtonProps> = (props) => {
    const { loading = false, image, showClear = false, onClear } = props;
    return <div className={styles.uploadButton}>
        <div className={ctx(styles.image, { [styles.loadedImage]: !!image })}>
            {loading ? <LoadingOutlined /> : (image ? image : <img src={uploadIcon} alt="upload icon" />)}
        </div>
        <div className={styles.secondColumn}>
            <div className={styles.actions}>
                <Button type='primary'><CogIcon color='white' size={14} className={styles.buttonIcon} icon='Backup' />{getTranslations('UPLOAD_FILE_UPLOAD')}</Button>
                {showClear ? <Button type="link" className={styles.clearButton} onClick={(e) => {
                    e.stopPropagation();
                    if (onClear) {
                        onClear();
                    }
                }}><CogIcon color='red' size={30} icon='Delete' /></Button> : null}
            </div>
            <div className={styles.fileTypes}>
                {getTranslations('UPLOAD_DEFAULT_IMAGE_TYPES')}
            </div>
        </div>
    </div>;
};

export const UploadImage: React.FunctionComponent<UploadImageProps> = (props) => {
    const { imageUrl, loading, onHandleChange, showClear, onClear } = props;
    return <Upload
        headers={{
            Authorization: cognitoUser?.getSignInUserSession()?.getAccessToken().getJwtToken() || '',
        }}
        method="POST"
        name="file"
        listType="picture-card"
        className={styles.uploadImage}
        showUploadList={false}
        action={`${BASE_URL[Microservices.FILES_SERVICE]}/files/uploadToS3`}
        beforeUpload={beforeUpload}
        onChange={(a) => {
            onHandleChange(a)
        }}
    >
        <UploadButton onClear={() => {
            if (onClear) {
                onClear();
            }
        }} showClear={showClear} image={imageUrl ? <img src={imageUrl} alt="avatar" style={{ width: '100%' }} /> : false} loading={loading} />
    </Upload>;
};

export const UploadButtonForProfile: React.FunctionComponent<UploadButtonForProfileProps> = (props) => {
    const { showClear = false, onClear } = props;
    const size = useDeviceSize();
    return <div className={styles.uploadButton}>
        <div className={styles.secondColumn}>
            <div className={styles.actions}>
                <Button type='primary'><CogIcon color='white' size={14} className={styles.buttonIcon} icon='Backup' />{getTranslations('UPLOAD_FILE_UPLOAD')}</Button>
                {showClear && size !== SIZES.PHONE ? <Button type="link" className={styles.clearButton} onClick={(e) => {
                    e.stopPropagation();
                    if (onClear) {
                        onClear();
                    }
                }}><CogIcon color='red' size={30} icon='Delete' /></Button> : null}
            </div>
            {size === SIZES.DESKTOP ? <div className={styles.fileTypes}>
                {getTranslations('UPLOAD_DEFAULT_IMAGE_TYPES')}
            </div> : null}
        </div>
    </div>;
};

export const UploadImageForProfile: React.FunctionComponent<UploadImageProps> = (props) => {
    const { onHandleChange, showClear, onClear } = props;
    const size = useDeviceSize();
    return <Upload
        headers={{
            Authorization: cognitoUser?.getSignInUserSession()?.getAccessToken().getJwtToken() || '',
        }}
        method="POST"
        name="file"
        listType="picture-card"
        className={ctx(styles.uploadImageForProfile, {
            [styles.mobile]: size === SIZES.PHONE,
        })}
        showUploadList={false}
        action={`${BASE_URL[Microservices.FILES_SERVICE]}/files/uploadToS3`}
        beforeUpload={beforeUpload}
        onChange={(a) => {
            onHandleChange(a)
        }}
    >
        <UploadButtonForProfile onClear={() => {
            if (onClear) {
                onClear();
            }
        }} showClear={showClear} />
    </Upload>;
};